import { Injectable } from '@angular/core';
import { ApiEndpointsService } from '../api-endpoint.service';
import { ApiHttpService } from '../api-http.service';
import { AppConst } from '@app/App.const';
import { ToasterService } from '../toaster.service';
import { fileDownload } from '../../utils/app-utils';
import type { customMappingDownload } from '../../utils/app-interface';

@Injectable({
  providedIn: 'root'
})
export class BulkDownloadService {

  public downloadJobIds: Map<string, string> = new Map();
  public displayJobIds: Map<string, string> = new Map();
  public getDownloadStatusInterval: any;
  public isDownloadStatusCheckerRunning: boolean = false;

  constructor(
    public apiEndPoint: ApiEndpointsService,
    public Api: ApiHttpService,
    public toasterService: ToasterService,
  ) { }

  // step 1: This method is called to initiate the download process.
  public initiateDownload(source: string, downloadOptions: string[], frDetails: {
    frId: string,
    tpaName: string,
    financialStartDate: string,
    financialEndDate: string,
    fundShortCodeData: string,
  }[]): void {
    console.log("Initiating download called");
    
    if(frDetails.length == 0){
       this.toasterService.showToasterError('Please select at least one Fund Request');
       return;
    }
    if(downloadOptions.length == 0){
       this.toasterService.showToasterError('Please select at least one download option');
       return;
    }

    // log warn if frDetails inner properties are null or undefined or empty quoted strings
    frDetails.forEach((fr) => { 
      if (!fr.frId || !fr.tpaName || !fr.financialStartDate || !fr.financialEndDate || !fr.fundShortCodeData) {
        console.warn("One or more properties of frDetails are null or undefined or empty quoted strings:", fr);
      }
    });

    const url = this.apiEndPoint.getBulkDownloadUrl();
    const payload = {
      "source": source,
      "downloadOptions": downloadOptions,
      "frDetails": frDetails
    }

    this.Api.callApi(
      AppConst.HttpMethods.POST,
      url,
      payload
    ).subscribe((res: any) => {
      if (res && res.statusCode == 200) {
        let jobId = res.jobId.toUpperCase();
        let defaultStatusMsg = "MSG_SENT_TO_TOPIC"
        this.downloadJobIds.set(jobId, defaultStatusMsg);
        this.displayJobIds.set(jobId, defaultStatusMsg);
        this.toasterService.showToasterSuccess('Download request initiated. Click the top right icon for status.');
        this.getDownloadStatus();
      }
      else {
        this.toasterService.showToasterError('Failed to initiate download');
      }
    }, error => {
      this.toasterService.showToasterError('Failed to initiate download');
    });
  }
  public initiateDownloadMapping(source: string, customMappingList: customMappingDownload[]) {
    const url = this.apiEndPoint.getBulkDownloadUrl();
    const payload = {
      source: source,
      customMappingList: customMappingList,
    }
    this.Api.callApi(
      AppConst.HttpMethods.POST,
      url,
      payload
    ).subscribe((res: any) => {
      if (res && res.statusCode === 200) {
        let jobId = res.jobId.toUpperCase();
        let defaultStatusMsg = "MSG_SENT_TO_TOPIC"
        this.downloadJobIds.set(jobId, defaultStatusMsg);
        this.displayJobIds.set(jobId, defaultStatusMsg);
        this.toasterService.showToasterSuccess('Download request initiated. Click the top right icon for status.');
        this.getDownloadStatus();
      }
      else {
        this.toasterService.showToasterError('Failed to initiate download');
      }
    }, error => {
      this.toasterService.showToasterError('Failed to initiate download');
    });
  }
    public getDownloadStatus(): void {
      if(!this.isDownloadStatusCheckerRunning){
        this.isDownloadStatusCheckerRunning = true;
        this.getDownloadStatusInterval = setInterval(() => {
          const jobIds = Array.from(this.downloadJobIds.keys()).join(',');
          const url = this.apiEndPoint.getDownloadStatusUrl(jobIds);
          if(this.downloadJobIds.size > 0) {
            // Call the API to get the status of the download jobs
            this.Api.callApi(
              AppConst.HttpMethods.GET,
              url
            ).subscribe((res: any) => {
                if(res && res.length > 0) {
                  res.forEach((job: any) => {
                  let jobId = job.jobId.toUpperCase();
                  let status = job.status;
                  this.downloadJobIds.set(jobId, status);
                  this.displayJobIds.set(jobId, status);
                    if(status === 'COMPLETED') {
                      let fileName = job.blobName;
                      this.logAndDeleteJobId(jobId);
                      this.downloadFile(jobId, fileName);
                    } else if (status === 'FAILED') {
                      this.toasterService.showToasterError(`Download request failed.`);
                      this.logAndDeleteJobId(jobId);
                      this.logAndDeleteDisplayJobId(jobId);
                    }
                  });
                }
            });
          }
          else {
            this.isDownloadStatusCheckerRunning = false;
            clearInterval(this.getDownloadStatusInterval);
            console.log("clearInterval finished");
          }
        },15000); // Check every 15 seconds
      }
    
  }


  // Step 3: This method is called to download the file once the job is completed.
  private downloadFile(jobId: string, fileName: string) {
    console.log("downloadFile called");

    this.displayJobIds.set(jobId, "SYSTEM_DOWNLOAD_STARTED");

    const url = this.apiEndPoint.getDownloadFileUrl(jobId);

    const observable = this.Api.callApi(
      AppConst.HttpMethods.GET,
      url,
      null,
      { responseType: 'blob', observe: 'response' }
    );

    observable.subscribe((response: any) => {
      if (response?.body) {
        let blob = new Blob([response.body], { type: 'application/octet-stream' });
        this.displayJobIds.set(jobId, "SYSTEM_DOWNLOAD_COMPLETED");
        fileDownload(fileName, blob);
        this.toasterService.showToasterSuccess(`Download request completed.`);
      } else {
        this.displayJobIds.set(jobId, "FAILED");
        this.toasterService.showToasterError(`Download request failed.`);
      }
      this.logAndDeleteDisplayJobId(jobId);
    });
    return

  }

  public get jobIdsInProgress(): { jobId: string, status: string }[] {
   // return array of objects with jobId and status
    return Array.from(this.displayJobIds.entries()).map(([jobId, status]) => ({ jobId: jobId.toUpperCase(), status }));
  }
  

   private logAndDeleteJobId(jobId: string) {
    console.log("Deleting jobId :" + jobId);
    this.downloadJobIds.delete(jobId);
  }

  private logAndDeleteDisplayJobId(jobId: string) {
    console.log("Deleting displayJobIds :" + jobId);
    this.displayJobIds.delete(jobId);
  }

  
}

